/**
 * FundingInstructionsPage
 * Displays today's funding instructions grouped by funding_window.
 * Shows a summary section with total instructions, total amount, and general status.
 * Route: /funding/instructions
 */

import { useMemo } from 'react';
import { RefreshCw, AlertTriangle, FileText, DollarSign, Clock } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { useFunding } from '@/features/funding/hooks';
import { FundingWindowCard } from '@/features/funding/components/FundingWindowCard';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';
import type { FundingInstruction } from '@/types/funding.types';

// ─── Constants ───────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FUNDING_INSTRUCTIONS },
  { label: 'Instrucciones', path: ROUTES.FUNDING_INSTRUCTIONS },
];

const WINDOW_ORDER = ['MORNING', 'AFTERNOON'] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getOverallStatus(instructions: FundingInstruction[]): {
  label: string;
  variant: 'default' | 'success' | 'warning' | 'danger' | 'info';
} {
  if (instructions.length === 0) {
    return { label: 'Sin instrucciones', variant: 'default' };
  }
  const allConfirmed = instructions.every((i) => i.status === 'CONFIRMED');
  if (allConfirmed) return { label: 'Completado', variant: 'success' };

  const hasPending = instructions.some((i) => i.status === 'PENDING_FUNDING');
  if (hasPending) return { label: 'Pendiente', variant: 'warning' };

  return { label: 'En proceso', variant: 'info' };
}

function groupByWindow(instructions: FundingInstruction[]): Map<string, FundingInstruction[]> {
  const groups = new Map<string, FundingInstruction[]>();
  for (const instruction of instructions) {
    const existing = groups.get(instruction.funding_window) ?? [];
    existing.push(instruction);
    groups.set(instruction.funding_window, existing);
  }
  return groups;
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={`stat-skeleton-${i.toString()}`} variant="rectangular" height={100} />
        ))}
      </div>
      {Array.from({ length: 2 }).map((_, i) => (
        <Skeleton key={`card-skeleton-${i.toString()}`} variant="rectangular" height={250} />
      ))}
    </div>
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function PageError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar las instrucciones de fondeo
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function FundingInstructionsPage() {
  const {
    pending,
    isLoadingPending,
    isErrorPending,
    refetchPending,
  } = useFunding();

  const totalAmount = useMemo(
    () => (pending ?? []).reduce((sum, i) => sum + i.amount_mxn, 0),
    [pending],
  );

  const overallStatus = useMemo(
    () => getOverallStatus(pending ?? []),
    [pending],
  );

  const groupedInstructions = useMemo(
    () => groupByWindow(pending ?? []),
    [pending],
  );

  if (isLoadingPending) {
    return (
      <div>
        <PageHeader
          title="Instrucciones de Fondeo"
          subtitle="Fondeo de desembolsos del dia"
          breadcrumbs={BREADCRUMBS}
        />
        <PageSkeleton />
      </div>
    );
  }

  if (isErrorPending) {
    return (
      <div>
        <PageHeader
          title="Instrucciones de Fondeo"
          subtitle="Fondeo de desembolsos del dia"
          breadcrumbs={BREADCRUMBS}
        />
        <PageError onRetry={() => refetchPending()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Instrucciones de Fondeo"
        subtitle="Fondeo de desembolsos del dia"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="funding-instructions" />
            <Button onClick={() => refetchPending()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <FileText size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total instrucciones</p>
              <p className="text-2xl font-semibold tabular-nums">
                {(pending ?? []).length}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <DollarSign size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monto total</p>
              <p className="text-2xl font-semibold tabular-nums">
                {formatCurrency(totalAmount, 'MXN', 'es-MX')}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Clock size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Estado general</p>
              <Badge variant={overallStatus.variant}>{overallStatus.label}</Badge>
            </div>
          </div>
        </Card>
      </div>

      {/* Instructions grouped by window */}
      <div className="space-y-6">
        {WINDOW_ORDER.map((window) => {
          const windowInstructions = groupedInstructions.get(window);
          if (!windowInstructions || windowInstructions.length === 0) return null;
          return (
            <FundingWindowCard
              key={window}
              window={window}
              instructions={windowInstructions}
            />
          );
        })}

        {(pending ?? []).length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
            <FileText size={48} className="mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">
              Sin instrucciones de fondeo
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              No hay instrucciones de fondeo programadas para hoy.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
